import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { InstallationDetailsStore, InstallationFeaturesStore } from '@store';
import {
  ActivateDeactivateCommands,
  DeviceIds,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeaturePropertyKey,
  IsActiveProperty,
  TariffBasedFinancialOptimizationStateCommands,
  TariffBasedFinancialOptimizationStateProperty,
} from '@vi-iot/client/index';
import { GTMService } from '@services';
import { AssistantSupportedLanguages } from 'src/app/@store/assistant';
import { DynamicAssistantPlan } from 'src/projects/client/apis/purchases/purchases.model';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-hems-financial-optimization',
  templateUrl: './hems-financial-optimization.component.html',
  styleUrls: ['./hems-financial-optimization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HemsFinancialOptimizationComponent {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureCommands = FeatureCommands;
  readonly assistantSupportedLanguages = AssistantSupportedLanguages;
  readonly dynamicAssistantPlan = DynamicAssistantPlan;
  readonly bcg: { label: string; isSubscriptionActive: boolean } = {
    label: '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_PRO',
    isSubscriptionActive: false,
  };

  @Input() isForecastBasedActive: boolean;
  @Input() hemsGateway: string;
  @Input() featureTariffBasedFinancialOptimizationState: Feature<
    TariffBasedFinancialOptimizationStateProperty,
    TariffBasedFinancialOptimizationStateCommands
  >;
  @Input() featureAllowBatteryChargingFromTheGrid: Feature<
    IsActiveProperty,
    ActivateDeactivateCommands
  >;
  @Input({ required: true }) featureForecastBased: Feature<
    IsActiveProperty,
    ActivateDeactivateCommands
  >;

  get bannerMessageKey(): string | null {
    const {
      subscriptionActive,
      tariffsActive,
    } = this.featureTariffBasedFinancialOptimizationState?.properties;

    const dynamicTariffSubscriptionActive = subscriptionActive?.value ?? false;
    const traiffDataAvailable = tariffsActive?.value ?? false;
    const hasBCG = !!this.featureAllowBatteryChargingFromTheGrid;

    if (!dynamicTariffSubscriptionActive && !hasBCG) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.SUBSCRIPTION_ACTIVE.EXPLAINATION.FALSE_TEXT';
    }

    if (dynamicTariffSubscriptionActive && !traiffDataAvailable && !hasBCG) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.TARIFFS_ACTIVE.EXPLAINATION.FALSE_TEXT';
    }

    if (!dynamicTariffSubscriptionActive && hasBCG) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.TARRIF_DATA_BATTERY_CHARGING_SUBSCRIPTION_REQUIRED_TEXT';
    }

    if (
      dynamicTariffSubscriptionActive &&
      hasBCG &&
      this.activeDAPlan === DynamicAssistantPlan.Basic
    ) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.BATTERY_CHARGING_SUBSCRIPTION_REQUIRED_TEXT';
    }

    if (
      dynamicTariffSubscriptionActive &&
      !traiffDataAvailable &&
      hasBCG &&
      this.activeDAPlan === DynamicAssistantPlan.Pro
    ) {
      return '+HEMS.SETTINGS.OPTIMIZATIONS.TARIFF_BASED_FINANCIAL_OPTIMIZATION.BATTERY_CHARGING_TARIFF_ACTIVE.EXPLANATION.FALSE_TEXT';
    }

    return null;
  }

  activeDAPlan: DynamicAssistantPlan;

  readonly dynamicAssistantLabel$ = this.installationDetailsStore.activeDynamicAssistantPlan$.pipe(
    tap((plan: DynamicAssistantPlan) => {
      this.activeDAPlan = plan;
      this.setBCGSubcriptionStatus(plan);
    }),
    map((plan: DynamicAssistantPlan) => this.getDAPlanTranslationKey(plan))
  );

  constructor(
    public installationFeaturesStore: InstallationFeaturesStore,
    public installationDetailsStore: InstallationDetailsStore,
    private gtmService: GTMService
  ) {}

  onTariffBasedFinancialOptimizationChange(
    change: MatSlideToggleChange,
    feature: Feature<
      TariffBasedFinancialOptimizationStateProperty,
      TariffBasedFinancialOptimizationStateCommands
    >
  ): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      change.checked,
      FeaturePropertyKey.active,
      FeatureActionFields.active,
      FeatureCommands.setActive,
      this.hemsGateway,
      DeviceIds.Hems
    );
    this.gtmService.track(
      change.checked ? 'onActivate' : 'onDeactivate',
      'Tariff based financial optimization state change',
      'hems'
    );
  }

  onActivateDeactivateFeature(
    change: MatSlideToggleChange,
    feature: Feature<IsActiveProperty, ActivateDeactivateCommands>,
    gtmMessage: string
  ): void {
    this.installationFeaturesStore.updateFeature(
      feature.feature,
      {},
      FeaturePropertyKey.active,
      null,
      change.checked ? FeatureCommands.activate : FeatureCommands.deactivate,
      this.hemsGateway,
      DeviceIds.Hems
    );
    this.gtmService.track(change.checked ? 'onActivate' : 'onDeactivate', gtmMessage, 'hems');
  }

  private getDAPlanTranslationKey(plan: DynamicAssistantPlan): string {
    if (!plan) {
      if (this.featureAllowBatteryChargingFromTheGrid) {
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_PRO';
      } else {
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_ON';
      }
    }

    switch (plan) {
      case DynamicAssistantPlan.Basic:
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_ON';
      case DynamicAssistantPlan.Pro:
        return '+HEMS.SETTINGS.OPTIMIZATIONS.DYNAMIC_ASSISTANT_PRO';
      default:
        return '';
    }
  }

  private setBCGSubcriptionStatus(plan: DynamicAssistantPlan): void {
    if (!plan || plan === DynamicAssistantPlan.Basic) {
      this.bcg.isSubscriptionActive = false;
    } else {
      this.bcg.isSubscriptionActive = true;
    }
  }
}
